import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Slide,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { ecommerceApi } from '../../../config/apiClient';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';
import LiveChatInbox from './liveChat/LiveChatInbox';
import LiveChatFlowEditor from './liveChat/LiveChatFlowEditor';
import LiveChatSettingsPanel from './liveChat/LiveChatSettingsPanel';
import { AGENT_NAME_KEY, BotSettings, QuickReply } from './liveChat/liveChatTypes';
import { setViewingLiveChatInbox } from '../../../services/ecommerceChatAlerts';

/**
 * Canlı destek: ziyaretçi önce yönlendirmeli soru akışından geçer,
 * akışın sonunda (veya istediğinde) görüşme temsilciye düşer.
 */
const LiveChatManagement: React.FC<{
  companyId?: string | null;
  /** Zil/toast'tan gelen "görüşmeyi aç" isteği; nonce her istekte değişir. */
  focus?: { sessionId: string | null; nonce: number } | null;
}> = ({ companyId, focus }) => {
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (focus) setTab(0);
  }, [focus]);

  // Görüşmeler ekranı açıkken global toast gösterilmez (ses yine çalar).
  useEffect(() => {
    setViewingLiveChatInbox(tab === 0);
    return () => setViewingLiveChatInbox(false);
  }, [tab]);
  const [schemaError, setSchemaError] = useState('');
  const [saved, setSaved] = useState<BotSettings | null>(null);
  const [settings, setSettings] = useState<BotSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingSample, setLoadingSample] = useState(false);
  const [waiting, setWaiting] = useState(0);
  const [agentName, setAgentName] = useState(() => localStorage.getItem(AGENT_NAME_KEY) || 'Müşteri Temsilcisi');

  useEffect(() => {
    ecommerceApi.get('/api/ecommerce/chat/bot-settings')
      .then((res) => {
        setSettings(res.data?.data);
        setSaved(res.data?.data);
      })
      .catch((error: any) => {
        if (error?.response?.data?.code === 'SCHEMA') setSchemaError(error.response.data.message);
      });
  }, [companyId]);

  const dirty = !!settings && !!saved && JSON.stringify(settings) !== JSON.stringify(saved);

  const persist = useCallback(async (next: BotSettings, message = 'Canlı destek ayarları kaydedildi') => {
    setSaving(true);
    try {
      const res = await ecommerceApi.post('/api/ecommerce/chat/bot-settings', next);
      setSettings(res.data?.data);
      setSaved(res.data?.data);
      notifyEcommerceSaved(message);
    } catch {
      notifyEcommerceSaved('Kaydedilemedi, tekrar deneyin', 'error');
    } finally {
      setSaving(false);
    }
  }, []);

  const patch = (value: Partial<BotSettings>) => setSettings((prev) => (prev ? { ...prev, ...value } : prev));

  // Vitrin anahtarı tek başına hemen kaydedilir; bekleyen diğer değişikliklere dokunmaz.
  const toggleEnabled = async (enabled: boolean) => {
    if (!saved || !settings) return;
    setSettings({ ...settings, enabled });
    setSaving(true);
    try {
      const res = await ecommerceApi.post('/api/ecommerce/chat/bot-settings', { ...saved, enabled });
      setSaved(res.data?.data);
      notifyEcommerceSaved(enabled ? 'Canlı destek vitrinde açıldı' : 'Canlı destek vitrinde kapatıldı');
    } catch {
      setSettings((prev) => (prev ? { ...prev, enabled: !enabled } : prev));
      notifyEcommerceSaved('Kaydedilemedi, tekrar deneyin', 'error');
    } finally {
      setSaving(false);
    }
  };

  const loadSample = async () => {
    if (!settings) return;
    if (settings.quickReplies.length > 0 && !window.confirm('Mevcut sorular örnek akışla değiştirilecek. Kaydetmeden önce gözden geçirebilirsiniz. Devam edilsin mi?')) return;
    setLoadingSample(true);
    try {
      const res = await ecommerceApi.get('/api/ecommerce/chat/bot-settings/sample-flow');
      const data = res.data?.data as { quickReplies: QuickReply[]; handoverTopics: BotSettings['handoverTopics'] };
      patch({ quickReplies: data.quickReplies, handoverTopics: data.handoverTopics });
    } finally {
      setLoadingSample(false);
    }
  };

  const changeAgentName = (value: string) => {
    setAgentName(value);
    localStorage.setItem(AGENT_NAME_KEY, value.trim() || 'Müşteri Temsilcisi');
  };

  return (
    <Box sx={{ pb: dirty ? 8 : 0 }}>
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SupportAgentIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={700}>Canlı Destek</Typography>
              <Typography variant="body2" color="text.secondary">
                Ziyaretçi soru akışıyla yönlendirilir, gerekirse temsilciye aktarılır
              </Typography>
            </Box>
          </Box>
          {settings && (
            <FormControlLabel
              labelPlacement="start"
              sx={{ m: 0 }}
              control={<Switch checked={settings.enabled} disabled={saving} onChange={(e) => toggleEnabled(e.target.checked)} />}
              label={
                <Box sx={{ pr: 1, textAlign: 'right' }}>
                  <Typography variant="body2" fontWeight={600}>{settings.enabled ? 'Vitrinde açık' : 'Vitrinde kapalı'}</Typography>
                  <Typography variant="caption" color="text.secondary">Sağ alttaki destek butonu</Typography>
                </Box>
              }
            />
          )}
        </Box>
        {schemaError && <Alert severity="warning" sx={{ mt: 2 }}>{schemaError}</Alert>}
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 1, minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600 } }}>
          <Tab
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                Görüşmeler
                {waiting > 0 && (
                  <Box
                    component="span"
                    sx={{
                      minWidth: 20,
                      height: 20,
                      px: 0.75,
                      borderRadius: 10,
                      bgcolor: 'warning.main',
                      color: 'warning.contrastText',
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: '20px',
                      textAlign: 'center',
                    }}
                  >
                    {waiting > 99 ? '99+' : waiting}
                  </Box>
                )}
              </Box>
            }
          />
          <Tab label="Soru akışı" />
          <Tab label="Ayarlar" />
        </Tabs>
      </Paper>

      {/* Görüşmeler sekmesi gizlense de canlı bağlantı ve bekleyen sayısı korunur. */}
      <Box sx={{ display: tab === 0 ? 'block' : 'none' }}>
        <LiveChatInbox
          companyId={companyId}
          agentName={agentName}
          templates={saved?.agentTemplates || []}
          topics={saved?.handoverTopics}
          onWaitingCount={setWaiting}
          onSchemaError={setSchemaError}
          focusSessionId={focus?.sessionId ?? null}
          focusNonce={focus?.nonce}
        />
      </Box>

      {tab !== 0 && !settings && !schemaError && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
      )}

      {tab === 1 && settings && (
        <LiveChatFlowEditor
          replies={settings.quickReplies}
          topics={settings.handoverTopics || []}
          onChange={(quickReplies) => patch({ quickReplies })}
          onLoadSample={loadSample}
          loadingSample={loadingSample}
        />
      )}

      {tab === 2 && settings && (
        <LiveChatSettingsPanel
          settings={settings}
          onChange={patch}
          agentName={agentName}
          onAgentNameChange={changeAgentName}
        />
      )}

      <Box sx={{ position: 'fixed', bottom: 24, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 1200, pointerEvents: 'none' }}>
      <Slide direction="up" in={dirty} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            pointerEvents: 'auto',
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderRadius: 3,
          }}
        >
          <Typography variant="body2" fontWeight={600}>Kaydedilmemiş değişiklikler var</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" color="inherit" onClick={() => setSettings(saved)} disabled={saving}>Vazgeç</Button>
            <Button size="small" variant="contained" disableElevation onClick={() => settings && persist(settings)} disabled={saving}>
              {saving ? 'Kaydediliyor' : 'Kaydet'}
            </Button>
          </Stack>
        </Paper>
      </Slide>
      </Box>
    </Box>
  );
};

export default LiveChatManagement;
